import { Card, Typography, Row, Col, Button, Divider, Form, Select, Switch, Checkbox, Cascader, notification, Image, Flex, Collapse } from "antd";
import React, { useState, useEffect } from "react";
import { DeleteOutlined, LoadingOutlined, DownOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import { removeProduct, addProduct, updateProduct } from "utilities/redux/slices/currentEstimate";
import { fetchProducts } from "utilities/redux/slices/productsSlice";
import axiosInstance from "utilities/axios";

const { Title, Text } = Typography;

const techLevelOrder = [
  "Premium Plus",
  "Premium",
  "Advanced",
  "Standard",
  "Everyday Low Price"
];

const getTechLevelOrder = (level) => {
  const idx = techLevelOrder.indexOf(level);
  return idx === -1 ? techLevelOrder.length : idx;
};

const DisplayCard = ({ product, setProductCount }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const finalized = useSelector((state) => state.currentEstimate.finalized);
  const estimate_id = useSelector((state) => state.currentEstimate.id);

  const [total, setTotal] = useState(0);
  
  const products = useSelector((state) => state.products.data);
  const currentProducts = useSelector((state) => state.currentEstimate.products);
  const discountsUpdating = useSelector((state) => state.currentEstimate.discountsUpdating);

  const [quantity, setQuantity] = useState(product?.quantity || 2);
  const [recommended, setRecommended] = useState(product?.recommended || false);
  const [discounts, setDiscounts] = useState(product?.discounts || []);
  const [financing, setFinancing] = useState(product?.financing || null);
  const [productOptions, setProductOptions] = useState([]);
  const [productPrice, setProductPrice] = useState(product?.price || 0);
  const [productPriceDiscount, setProductPriceDiscount] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchProducts({}));

    axiosInstance
      .get("/products/cascade", {
        params: {
          estimate_id
        }
      })
      .then((response) => {
        setProductOptions(response.data);
        setLoading(false);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: "Failed to fetch product options",
        });
      });
  }, [dispatch]);

  useEffect(() => {
    if (product !== undefined && product !== null) {
      setQuantity(product.quantity);
      setRecommended(product.recommended || false);

      form.setFieldsValue({
        product: product?.identifier,
        quantity: product?.quantity === 2 ? true : false,
      });
    };

    setDiscounts(product?.discounts || []);
    setFinancing(product?.financing || null);
    setProductPrice(product?.price || 0);
    setTotal(0);
    setProductPriceDiscount(null);

    var tempTotal = product?.price || 0;
    tempTotal = product?.discounts.find(discount => discount.product_price !== undefined && discount.product_price !== null)?.product_price || tempTotal;

    for (let i = 0; i < product?.discounts.length; i++) {
      const { discount_type, discount_amount } = product?.discounts[i];
      
      if (product?.discounts[i]?.product_price !== undefined && product?.discounts[i]?.product_price !== null) {
        setProductPrice(product?.discounts[i]?.product_price);
        setProductPriceDiscount(product?.discounts[i]);
      } else {
        tempTotal -= discount_amount;
      }
    };

    setTotal(tempTotal);
  }, [product]);

  const onChange = (changedValues, allValues) => {
    if (product === undefined) {
      dispatch(addProduct({ productId: allValues.product[allValues.product.length - 1], quantity: allValues.quantity ? 2 : 1 }));
    } else {
      dispatch(updateProduct({ productId: product.id, newProductId: allValues.product[allValues.product.length - 1], quantity: allValues.quantity ? 2 : 1 }));
    }
  };

  const itemStyling = {
    header: {
      alignItems: "center",
      padding: '4px 0',
    },
    body: {
      padding: '4px 0 4px 52px'
    }
  }

  const items = [
    {
      key: "clinic-care",
      label: (
        <Flex align="center" style={{ margin: 0 }}>
          <div style={{ marginRight: 12, display: 'flex', alignItems: 'center' }}>
            <img src={'/assets/3-Years.png'} alt="3 Year Warranty Icon" style={{ width: 40, height: 40 }} />
          </div>
          <h4 style={{ margin: 0 }}>Lifetime Complimentary In-Clinic Care</h4>
        </Flex>
      ),
      children: (
        <>
          <p style={{ margin: '0', padding: '0' }}>Clinic visits, adjustments, in-house repairs, service, cleaning and select supplies.</p>
          <ul style={{ margin: '0' }}>
            <li><span style={{ color: '#002c5f', fontWeight: 600 }}>3 years</span> of all-inclusive manufacturer's warranty on most devices</li>
            <li><span style={{ color: '#002c5f', fontWeight: 600 }}>2 years</span> of loss and damage insurance on most devices</li>
          </ul>
        </>
      ),
      styles: itemStyling
    },
    {
      key: "remote-care",
      label: (
        <Flex align="center" style={{ margin: 0 }}>
          <div style={{ marginRight: 12, display: 'flex', alignItems: 'center' }}>
            <img src={'/assets/Client-Care-and-Support.png'} alt="Remote Hearing Icon" style={{ width: 40, height: 40 }} />
          </div>
          <h4 style={{ margin: 0 }}>Client Care and Support</h4>
        </Flex>
      ),
      children: (
        <p style={{ margin: '0', padding: '0' }}>Access to remote hearing care professionals - live technical support via phone, online chat or email.</p>
      ),
      styles: itemStyling
    },
    {
      key: "walk-in",
      label: (
        <Flex align="center" style={{ margin: 0 }}>
          <div style={{ marginRight: 12, display: 'flex', alignItems: 'center' }}>
            <img src={'/assets/Walk-in.png'} alt="Walk-in Hours Icon" style={{ width: 40, height: 40 }} />
          </div>
          <h4 style={{ margin: 0 }}>Walk-In Hour</h4>
        </Flex>
      ),
      children: (
        <p style={{ margin: '0', padding: '0' }}>Daily walk-in hour from 1-2pm for clean and checks, service and supply pickup <i>(subject to clinic availability).</i> No appointments necessary.</p>
      ),
      styles: itemStyling
    },
    {
      key: "satisfaction",
      label: (
        <Flex align="center" style={{ margin: 0 }}>
          <div style={{ marginRight: 12, display: 'flex', alignItems: 'center' }}>
            <img src={'/assets/Satisfaction-Guarantee.png'} alt="90 Day Satisfaction Guarantee Icon" style={{ width: 40, height: 40 }} />
          </div>
          <h4 style={{ margin: 0 }}>Satisfaction Guarantee</h4>
        </Flex>
      ),
      children: (
        <p style={{ margin: '0', padding: '0' }}>With our 90-day return or exchange policy.</p>
      ),
      styles: itemStyling
    }
  ];

  if (product === undefined) {
    return (
      <Card
        style={{ height: "100%" }}
        styles={{
          'body': {
            display: "flex",
            flexDirection: "column",
            height: "100%",
            padding: 24
          }
        }}
        loading={discountsUpdating}
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={onChange}
          initialValues={{
            product: product?.identifier,
            quantity: quantity === 2 ? true : false,
          }}
        >
            <Row gutter={[16, 16]} style={{ width: "100%" }}>
              <Col span={18}>
                <Form.Item
                  name="product"
                  rules={[{ required: true, message: "Please select a product" }]}
                >
                  <Cascader
                    options={productOptions}
                    placeholder="Select a product"
                    style={{ width: "100%" }}
                    displayRender={label => label.join(" ")}
                    disabled={loading}
                    suffixIcon={loading ? <LoadingOutlined /> : <DownOutlined />}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="quantity"
                  rules={[{ required: true, message: "Please select quantity" }]}
                >
                  <Switch checkedChildren="Binaural" unCheckedChildren="Monaural" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
        </Form>
        <div style={{ marginTop: "auto" }}>
          {!finalized && (
          <div style={{ marginTop: 24 }}>
            <Button
              icon={<DeleteOutlined />}
              onClick={() => {
                setProductCount((prev) => prev - 1);
              }}
              block
              type="default"
              danger
            >
              Remove
            </Button>
          </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <Card
      loading={discountsUpdating}
      style={{ height: "100%" }}
      styles={{
        'body': {
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={onChange}
        initialValues={{
          product: product?.identifier,
          quantity: quantity === 2 ? true : false,
        }}
      >
        <div style={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Row gutter={[16, 16]} style={{ width: "100%", marginTop: 0 }}>
            <Col span={18}>
              <Form.Item
                name="product"
                rules={[{ required: true, message: "Please select a product" }]}
              >
                <Cascader
                  options={productOptions}
                  placeholder="Select a product"
                  style={{ width: "100%" }}
                  displayRender={label => label.join(" ")}
                  disabled={loading}
                  suffixIcon={loading ? <LoadingOutlined /> : <DownOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="quantity"
                rules={[{ required: true, message: "Please select quantity" }]}
              >
                <Switch checkedChildren="Binaural" unCheckedChildren="Monaural" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </div>
      </Form>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <Title level={3} style={{ marginBottom: 4, marginTop: 0 }}>
          {product?.name}
        </Title>
        {product?.tech_level && (
          <>
            <Text type="primary">{product?.tech_level}</Text>
            <br />
          </>
        )}
        {product?.quantity === 1 && <Text type="secondary">Monaural</Text>}
        {product?.quantity === 2 && <Text type="secondary">Binaural</Text>}
      </div>

      {/* <div style={{ display: "flex", justifyContent: "center", marginBottom: 0 }}>
        <div style={{ width: 160, height: 160, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Image
            src={product?.photo}
            fallback="data:image/png;base64,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"
          />
        </div>
      </div> */}

      <Title level={4} style={{ margin: 0, textAlign: "center" }}>
        360 AfterCare
      </Title>
      <Collapse
        ghost
        accordion
        expandIconPosition="end"
        expandIcon={({ isActive }) => (
          <Flex align="center" justify="center" style={{ width: "100%" }}>
            <DownOutlined rotate={isActive ? 180 : 0} />
          </Flex>
        )}
        style={{ marginTop: 8, marginBottom: 16, padding: '0 64px' }}
        items={items}
      />
        
      <Flex align="center" justify="center" style={{ margin: "auto" }}>
        <Checkbox
          checked={recommended}
          onChange={(e) => {
            const isChecked = e.target.checked;
            setRecommended(isChecked);
            dispatch(updateProduct({ productId: product.id, newProductId: product.id, quantity: product.quantity, recommended: isChecked }));
          }}
        >
          Recommend Product
        </Checkbox>
      </Flex>

      <div style={{ margin: "auto 0", padding: " 8px 0" }}>
        <div style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <Text strong>
              {productPriceDiscount && !productPriceDiscount.hide_discount_name
                ? `Price (${productPriceDiscount.discount_name})`
                : 'Price'}
            </Text>
            <Text>${productPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
          </div>

          {discounts.map((discount, index) => {
            if (discount !== productPriceDiscount) {
              return (
                <div key={index} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <Text>{discount.discount_name}</Text>
                    <Text>- ${discount.discount_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                </div>
              );
            }
          })}

          <Divider style={{ marginTop: 16, marginBottom: 16 }} />

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong style={{ fontSize: 18 }}>Total</Text>
            <Text style={{ fontSize: 18 }}>
              ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
          </div>
        </div>
      </div>
      <div style={{ textAlign: "center", margin: "auto" }}>
        {financing && (
          <Text>Financing available: ${financing.monthly_payment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/month for {financing.term} months at {financing.interest_rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}% APR with {financing.down_payment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}% (${(financing.down_payment / 100 * total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) down</Text>
        )}
      </div>
      <div style={{ marginTop: "auto"}}>
        {!finalized && (
          <div style={{ marginTop: 24 }}>
            <Button
              icon={<DeleteOutlined />}
              onClick={() => {
                setProductCount((prev) => prev - 1);

                if (product !== undefined || product !== null) {
                  dispatch(removeProduct(product.id));
                  
                }
              }}
              block
              type="default"
              danger
            >
              Remove
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
};

export default DisplayCard;